import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Paperclip, FileText, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { money } from "../lib/format";
import { AttachmentsDialog, AttachmentsPanel } from "../components/InvoiceAttachments";
import { StatusPill } from "./DashboardPage";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { useApprovalQueue } from "../lib/approvals";

const fundPoolLabel = (invoice) => {
  const source = invoice?.fund_source || "ERR";
  if (source === "PPR") return "PPR";
  if (source === "SPLIT") return "ERR and PPR";
  return "ERR";
};

export default function ApprovalsPage() {
  const { queue, loading, refresh, isApprover } = useApprovalQueue();
  const [confirm, setConfirm] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [tab, setTab] = useState("queue");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/invoices/decision-history", {
        params: { q: search, status: statusFilter },
      });
      setHistory(data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setHistoryLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab, loadHistory]);

  const decide = useCallback(async () => {
    if (!isApprover || !confirm) return;
    setBusy(true);
    try {
      await api.put(`/invoices/${confirm.invoice.id}/decision`, {
        status: confirm.status, note,
      });
      toast.success(`Invoice ${confirm.invoice.invoice_number} ${confirm.status}`);
      setConfirm(null);
      setNote("");
      refresh();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(false); }
  }, [confirm, isApprover, note, refresh]);

  const total = queue.reduce((a, b) => a + b.amount, 0);
  const oldest = queue.length ? queue[0].submitted_at.slice(0, 10) : "—";

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10 md:py-14" data-testid="approvals-page">
      <div className="mb-10">
        <div className="eyebrow mb-2">Approval queue</div>
        <h1 className="font-heading font-black text-5xl md:text-6xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
          {isApprover ? "Waiting on you" : "Approval status"}
        </h1>
        <p className="mt-3 text-[15px]" style={{ color: "var(--muted)" }}>
          {isApprover
            ? "Vendor invoices from your company that need your approval before the program administrator can pay them."
            : "Review invoice statuses for your company. Designated approvers can make decisions."}
        </p>
      </div>

      <div className="border grid-borders grid grid-cols-1 md:grid-cols-3" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <Kpi label={isApprover ? "Awaiting you" : "Awaiting decision"} value={String(queue.length)} sub="Invoices pending approval" testid="kpi-awaiting" />
        <Kpi label="Value in queue" value={money(total)} sub="Total pending amount" testid="kpi-value" />
        <Kpi label="Oldest submission" value={oldest} sub="First in line" testid="kpi-oldest" />
      </div>

      <div className="mt-8 flex items-center gap-1 border-b" style={{ borderColor: "var(--border)" }}>
        {[["queue", "Awaiting Approval"], ["history", "Decision History"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="px-4 py-3 text-sm font-medium"
            style={{
              color: tab === key ? "var(--ink)" : "var(--muted)",
              borderBottom: tab === key ? "2px solid var(--ink)" : "2px solid transparent",
            }}
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "queue" && (
      <div className="mt-8 border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b eyebrow" style={{ borderColor: "var(--border)" }}>
          <div className="col-span-3">Vendor</div>
          <div className="col-span-2">Invoice #</div>
          <div className="col-span-2">Submitted by</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-3 text-right">Amount / decision</div>
        </div>
        <div className="divide-y" style={{ borderColor: "var(--border)" }}>
          {queue.map((inv) => (
            <motion.div
              key={inv.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-12 px-6 py-4 items-center text-sm gap-3"
              data-testid={`approval-row-${inv.id}`}
            >
              <div className="col-span-12 md:col-span-3 font-medium" style={{ color: "var(--ink)" }}>
                <div className="flex items-center gap-2"><FileText size={14} style={{ color: "var(--muted)" }} /> {inv.vendor_name}
                  {inv.fund_source && inv.fund_source !== "ERR" && (
                    <span className="pill text-[9px]" data-testid={`queue-fund-${inv.id}`}
                      title={inv.fund_source === "SPLIT" ? `ERR ${money(inv.err_amount)} + PPR ${money(inv.ppr_amount)}` : "Purchase Power Refunds"}>
                      {inv.fund_source}
                    </span>
                  )}
                </div>
                {inv.description && <div className="text-xs mt-1 truncate" style={{ color: "var(--muted)" }}>{inv.description}</div>}
              </div>
              <div className="col-span-6 md:col-span-2 font-mono text-xs" style={{ color: "var(--muted)" }}>{inv.invoice_number}</div>
              <div className="col-span-6 md:col-span-2" style={{ color: "var(--muted)" }}>{inv.submitted_by}</div>
              <div className="col-span-6 md:col-span-2 flex items-center gap-1" style={{ color: "var(--muted)" }}>
                <Clock size={12} /> {inv.invoice_date}
              </div>
              <div className="col-span-12 md:col-span-3 flex items-center justify-end gap-3 flex-wrap">
                <span className="font-heading font-bold text-lg" style={{ color: "var(--ink)" }}>{money(inv.amount)}</span>
                <button
                  onClick={() => setActiveInvoice(inv)}
                  className="btn-ghost !p-2 flex items-center gap-1 text-xs"
                  aria-label={`Documents for invoice ${inv.invoice_number}`}
                  data-testid={`approval-attachments-${inv.id}`}
                >
                  <Paperclip size={14} /> {inv.attachment_count || 0}
                </button>
                {isApprover && <>
                  <button
                    onClick={() => { setConfirm({ invoice: inv, status: "rejected" }); setNote(""); }}
                    className="btn-ghost !px-2 !py-1 text-xs flex items-center gap-1"
                    data-testid={`reject-${inv.id}`}
                  >
                    <X size={13} /> Reject
                  </button>
                  <button
                    onClick={() => { setConfirm({ invoice: inv, status: "approved" }); setNote(""); }}
                    className="btn-primary !px-3 !py-1.5 text-xs flex items-center gap-1"
                    data-testid={`approve-${inv.id}`}
                  >
                    <Check size={13} /> Approve
                  </button>
                </>}
              </div>
            </motion.div>
          ))}
          {!queue.length && (
            <div className="p-12 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="approvals-empty">
              {loading ? "Loading…" : "Nothing is waiting on you. All invoices have been reviewed."}
            </div>
          )}
        </div>
      </div>
      )}

      {tab === "history" && (
        <div className="mt-8" data-testid="history-section">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              className="input-flat flex-1"
              placeholder="Search vendor, invoice # or note"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="history-search"
            />
            <div className="flex items-center gap-1">
              {["all", "approved", "rejected", "paid"].map((value) => (
                <button
                  key={value}
                  onClick={() => setStatusFilter(value)}
                  className="px-3 py-2 text-xs border capitalize"
                  style={{
                    borderColor: "var(--border)",
                    background: statusFilter === value ? "var(--ink)" : "transparent",
                    color: statusFilter === value ? "var(--surface)" : "var(--muted)",
                  }}
                  data-testid={`history-filter-${value}`}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
          <div className="border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b eyebrow" style={{ borderColor: "var(--border)" }}>
              <div className="col-span-3">Vendor</div>
              <div className="col-span-2">Invoice #</div>
              <div className="col-span-3">Note</div>
              <div className="col-span-2">Decided</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {history.map((inv) => (
                <div key={inv.id} className="grid grid-cols-12 px-6 py-4 items-center text-sm gap-3" data-testid={`history-row-${inv.id}`}>
                  <div className="col-span-12 md:col-span-3 font-medium" style={{ color: "var(--ink)" }}>
                    {inv.vendor_name}
                    <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>by {inv.submitted_by}</div>
                  </div>
                  <div className="col-span-6 md:col-span-2 font-mono text-xs" style={{ color: "var(--muted)" }}>{inv.invoice_number}</div>
                  <div className="col-span-12 md:col-span-3 text-xs" style={{ color: "var(--muted)" }}>{inv.decision_note || "—"}</div>
                  <div className="col-span-6 md:col-span-2 text-xs" style={{ color: "var(--muted)" }}>
                    {inv.decided_at ? inv.decided_at.slice(0, 10) : "—"}
                  </div>
                  <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-3">
                    <StatusPill status={inv.status} />
                    <span className="font-heading font-bold text-lg" style={{ color: "var(--ink)" }}>{money(inv.amount)}</span>
                  </div>
                </div>
              ))}
              {!history.length && (
                <div className="p-12 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="history-empty">
                  {historyLoading ? "Loading…" : "No decisions recorded yet."}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <Dialog open={Boolean(confirm)} onOpenChange={(isOpen) => !isOpen && setConfirm(null)}>
        <DialogContent className="!rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="approval-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">
              {confirm?.status === "approved" ? "Approve this invoice?" : "Reject this invoice?"}
            </DialogTitle>
            <DialogDescription>
              {confirm?.invoice.vendor_name} · {confirm?.invoice.invoice_number} · {money(confirm?.invoice.amount || 0)}
              {confirm?.status === "approved"
                ? ` — the program administrator will be able to pay it from your ${fundPoolLabel(confirm?.invoice)} pool.`
                : " — the submitter will be notified it was not approved."}
            </DialogDescription>
          </DialogHeader>
          {confirm?.invoice && (
            <div data-testid="approval-attachment-preview">
              <div className="eyebrow mb-2">Invoice document</div>
              <AttachmentsPanel
                invoice={confirm.invoice}
                canDelete={false}
                canUpload={false}
                autoPreview
                previewHeight={360}
              />
            </div>
          )}
          <div>
            <label className="field-label">Note (optional)</label>
            <textarea
              className="input-flat resize-none"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason or context for this decision"
              data-testid="approval-note"
            />
          </div>
          <DialogFooter>
            <button type="button" className="btn-ghost" onClick={() => setConfirm(null)} data-testid="approval-cancel">
              Cancel
            </button>
            <button type="button" className="btn-primary" disabled={busy} onClick={decide} data-testid="approval-confirm">
              {busy ? "Saving…" : confirm?.status === "approved" ? "Yes, Approve" : "Yes, Reject"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachmentsDialog invoice={activeInvoice} onClose={() => setActiveInvoice(null)} onChanged={refresh} />
    </div>
  );
}

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="grid-cell p-8" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className="stat-num text-4xl md:text-5xl mt-3">{value}</div>
      <div className="text-sm mt-2" style={{ color: "var(--muted)" }}>{sub}</div>
    </div>
  );
}
